import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";

export const useApi = (path) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(path).then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e)); setData([]); }), [path]);
  useEffect(() => {
    load();
  }, [load]);
  return [data, load, setData];
};

export const act = async (fn, ok) => {
  try {
    const r = await fn();
    if (ok) toast.success(ok);
    return r?.data ?? true;
  } catch (e) {
    toast.error(errMsg(e));
    return null;
  }
};

export const PageHead = ({ title, sub, children }) => (
  <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
    <div><h1 className="font-display text-4xl sm:text-5xl" data-testid="admin-page-title">{title}</h1>{sub && <p className="mt-1 text-sm text-zinc-400">{sub}</p>}</div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Stat = ({ label, value, testid, hint }) => (
  <div className="border border-white/10 bg-[#141414] p-5" data-testid={testid}>
    <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
    <p className="mt-2 font-display text-3xl text-white">{value}</p>
    {hint && <p className="mt-1 text-[11px] text-zinc-500">{hint}</p>}
  </div>
);

export const Table = ({ cols, rows, testid = "admin-table", empty = "No records yet." }) => (
  <div className="overflow-x-auto border border-white/10" data-testid={testid}>
    <table className="w-full min-w-[640px] text-left text-sm">
      <thead className="bg-[#141414] font-mono text-[10px] uppercase tracking-widest text-zinc-500"><tr>{cols.map((c) => <th key={c.k || c.h} className="px-3 py-3">{c.h}</th>)}</tr></thead>
      <tbody>
        {!rows ? <tr><td className="px-3 py-6 text-zinc-500" colSpan={cols.length}>Loading…</td></tr> : !rows.length ? <tr><td className="px-3 py-6 text-zinc-500" colSpan={cols.length}>{empty}</td></tr> :
          rows.map((r, i) => <tr key={r.id || r.sku || r.code || r.email || i} className="border-t border-white/5 hover:bg-white/[0.02]">{cols.map((c) => <td key={c.k || c.h} className="px-3 py-2.5 align-top">{c.r ? c.r(r) : r[c.k]}</td>)}</tr>)}
      </tbody>
    </table>
  </div>
);

export const Badge = ({ s }) => {
  const color = /sandbox|pending|processing|draft|review/.test(s) ? "text-amber-300 border-amber-300/40" : /fail|cancel|expired|refund|not_/.test(s) ? "text-red-400 border-red-400/40" : /paid|completed|delivered|sent|live|active|approved|received|ok/.test(s) ? "text-emerald-400 border-emerald-400/40" : "text-zinc-300 border-white/20";
  return <span className={`whitespace-nowrap border px-2 py-0.5 font-mono text-[10px] uppercase ${color}`} data-testid="status-badge">{String(s ?? "-").replace(/_/g, " ")}</span>;
};

export const Field = ({ label, children }) => <label className="block text-[11px] uppercase tracking-widest text-zinc-400">{label}<div className="mt-1 normal-case tracking-normal">{children}</div></label>;

export const SimpleForm = ({ fields, initial = {}, onSubmit, submit = "Save", testid = "admin-form" }) => {
  const [f, setF] = useState(initial);
  return (
    <form onSubmit={async (e) => { e.preventDefault(); if (await onSubmit(f)) setF(initial); }} className="grid gap-3 border border-white/10 bg-[#141414] p-4 sm:grid-cols-2 lg:grid-cols-4" data-testid={testid}>
      {fields.map(([k, label, type = "text", opts]) => (
        <Field key={k} label={label}>
          {type === "select" ? <select className="field" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`${testid}-${k}`}>{opts.map((o) => <option key={o} value={o}>{o}</option>)}</select>
            : type === "checkbox" ? <input type="checkbox" checked={!!f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} className="accent-[#C8A45D]" data-testid={`${testid}-${k}`} />
            : <input className="field" type={type} step="any" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })} data-testid={`${testid}-${k}`} />}
        </Field>
      ))}
      <div className="flex items-end"><button className="btn-gold w-full" data-testid={`${testid}-submit`}>{submit}</button></div>
    </form>
  );
};
